import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
} from "@jhool-io/fe-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIsMutating } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { z } from "zod";
import {
    useFetchClientBasicAndPersonalInfo,
    useFetchClientList,
} from "../../../../hooks/queries/client";
import { displayNameInRightFormat } from "../../../../utils/helpers";
import {
    useGetLabCategories,
    useGetLabTests,
} from "../../hooks/e-prescribe.queries";

import { IClient } from "../../../../utils/types/client";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import CheckIcon from "../../../../icons/Check";
import { useDebounce } from "../../../../hooks/helpers";
import { useAppSelector } from "../../../../hooks/useRedux";
import {
    useCreateDraftLabRequest,
    useSignLabRequest,
    useUpdateLabRequest,
} from "../../hooks/e-prescribe.mutations";
import {
    ICreateLabRequestForm,
    IEditDraftLabRequestPayload,
    ILabRequest,
    ISignLabRequestForm,
    ISignLabRequestPayload,
} from "../../types/e-prescribe.types";
import useToast from "../../../../hooks/useToast";
import SignLabRequestForm, {
    SIGN_LAB_REQUEST_FORM_ID,
} from "../SignLabRequestForm/SignLabRequestForm";

const createLabRequestSchema: z.ZodType<ICreateLabRequestForm> = z.object({
    lab_company_id: z.string().uuid().nullable(),
    client_id: z.string().uuid("Select a client"),
    provider_id: z.string().uuid("A provider is required"),
    note_id: z.string().uuid().nullable(),
    selected_lab_test_ids: z
        .array(z.string().uuid())
        .min(1, "Select at least one lab test"),
});

interface CreateLabRequestFormProps {
    labRequest?: ILabRequest;
}

export default function CreateLabRequestForm({
    labRequest,
}: CreateLabRequestFormProps) {
    const [showClientPopover, setShowClientPopover] = useState(false);
    const [showSignDialog, setShowSignDialog] = useState(false);
    const [clientSearchString, setClientSearchString] = useState("");
    const [selectedClient, setSelectedClient] = useState<Pick<
        IClient,
        "first_name" | "last_name" | "client_id"
    > | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { practice } = useAppSelector((state) => state.userPractice);
    const draftLabRequestId = useRef(labRequest?.lab_request_id);

    const debouncedClientSearchString = useDebounce(clientSearchString, 300);

    const clientId =
        labRequest?.client_id ?? searchParams.get("client_id") ?? "";
    const noteId = labRequest?.note_id ?? searchParams.get("note_id") ?? "";

    const labCategoryFilter = searchParams.get("lab_category") || "";

    const { handleSubmit, setValue, watch } = useForm<ICreateLabRequestForm>({
        resolver: zodResolver(createLabRequestSchema),
        defaultValues: {
            client_id: clientId ?? "",
            provider_id: labRequest?.provider_id ?? practice?.provider_id ?? "",
            lab_company_id: null,
            note_id: noteId || null,
            selected_lab_test_ids:
                labRequest?.tests.map((test) => test.lab_test_id) ?? [],
        },
    });

    const selectedTests = watch("selected_lab_test_ids");

    const createDraftLabRequest = useCreateDraftLabRequest();
    const updateLabRequest = useUpdateLabRequest();
    const signLabRequest = useSignLabRequest();

    const clientDetails = useFetchClientBasicAndPersonalInfo(
        clientId ?? "",
        Boolean(clientId)
    );

    const clients = useFetchClientList(
        {
            limit: 20,
            search_string: debouncedClientSearchString,
            provider_id: practice?.provider_id ?? "",
        },
        Boolean(!clientId)
    );

    const labCategoriesResponse = useGetLabCategories({
        is_active: true,
    });
    const labTestsResponse = useGetLabTests({
        lab_category_id: labCategoryFilter,
        is_active: true,
    });

    const labCategories = useMemo(
        () => labCategoriesResponse.data?.data ?? [],
        [labCategoriesResponse.data?.data]
    );

    const selectedLabCategory = labCategories.find(
        (category) => category.lab_category_id === labCategoryFilter
    );

    const labTests = useMemo(
        () => labTestsResponse?.data?.data ?? [],
        [labTestsResponse?.data?.data]
    );
    const availableTestsByCategory = useMemo(
        () =>
            labCategories
                .map((category) => ({
                    category,
                    tests: labTests.filter(
                        (test) =>
                            test.lab_category_id === category.lab_category_id
                    ),
                }))
                .filter(({ tests }) => tests.length > 0),
        [labCategories, labTests]
    );

    const clientName = clientDetails?.data?.data
        ? `${displayNameInRightFormat({
              firstName: clientDetails?.data?.data.first_name,
              lastName: clientDetails?.data?.data.last_name,
          })}`
        : null;

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else {
            searchParams.set(paramName, value);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleTestChange = (labTestId: string, checked: boolean) => {
        const nextSelectedLabTestIds = checked
            ? [...new Set([...selectedTests, labTestId])]
            : selectedTests.filter(
                  (selectedLabTestId) => selectedLabTestId !== labTestId
              );

        setValue("selected_lab_test_ids", nextSelectedLabTestIds, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const handleCategoryTestsChange = (
        categoryLabTestIds: string[],
        checked: boolean
    ) => {
        const categoryLabTestIdSet = new Set(categoryLabTestIds);
        const nextSelectedLabTestIds = checked
            ? [...new Set([...selectedTests, ...categoryLabTestIds])]
            : selectedTests.filter(
                  (labTestId) => !categoryLabTestIdSet.has(labTestId)
              );

        setValue("selected_lab_test_ids", nextSelectedLabTestIds, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const showFormErrorToast = (
        formErrors: FieldErrors<ICreateLabRequestForm>
    ) => {
        const message =
            formErrors.client_id?.message ??
            formErrors.provider_id?.message ??
            formErrors.note_id?.message ??
            formErrors.selected_lab_test_ids?.message ??
            "Complete the required fields";

        toast({
            mode: "error",
            message,
        });
    };

    const getUpdatePayload = (
        values: ICreateLabRequestForm
    ): IEditDraftLabRequestPayload => ({
        lab_company_id: values.lab_company_id,
        selected_lab_test_ids: values.selected_lab_test_ids,
    });

    const persistDraft = (
        values: ICreateLabRequestForm,
        onSuccess: (labRequestId: string) => void
    ) => {
        const mutationOptions = {
            onSuccess: (response: { data: { lab_request_id: string } }) => {
                const labRequestId = response.data.lab_request_id;
                onSuccess(labRequestId);
            },
            onError: (error: { response?: { data: { message?: string } } }) => {
                toast({
                    mode: "error",
                    message:
                        error.response?.data.message ??
                        "Could not save lab request",
                });
            },
        };

        if (labRequest) {
            updateLabRequest.mutate(
                {
                    labRequestId: labRequest.lab_request_id,
                    payload: getUpdatePayload(values),
                },
                mutationOptions
            );
            return;
        }

        createDraftLabRequest.mutate(values, mutationOptions);
    };

    const saveDraftLabRequest = (values: ICreateLabRequestForm) => {
        persistDraft(values, () => {
            toast({
                mode: "success",
                message: "Lab request draft saved successfully",
            });
            navigate("/lab-requests");
        });
    };

    const saveDraftBeforeSigning = (values: ICreateLabRequestForm) => {
        persistDraft(values, (labRequestId) => {
            draftLabRequestId.current = labRequestId;
            setShowSignDialog(true);
        });
    };

    const signSavedLabRequest = (values: ISignLabRequestForm) => {
        if (!draftLabRequestId.current) {
            toast({
                mode: "error",
                message: "Lab request must be saved before signing",
            });
            return;
        }

        const comment = values.comment.trim();
        const payload: ISignLabRequestPayload = {
            diagnosis_codes: values.diagnosis_codes.length
                ? values.diagnosis_codes
                : undefined,
            comment: comment || null,
            signature: values.signature.trim(),
        };

        signLabRequest.mutate(
            {
                labRequestId: draftLabRequestId.current,
                payload,
            },
            {
                onSuccess: () => {
                    toast({
                        mode: "success",
                        message: "Lab request signed successfully",
                    });
                    setShowSignDialog(false);
                    navigate("/lab-requests");
                },
                onError: (error) => {
                    toast({
                        mode: "error",
                        message:
                            error.response?.data.message ??
                            "Lab request could not be signed",
                    });
                },
            }
        );
    };

    const createLabRequestIsMutating = useIsMutating([
        "create-draft-lab-request",
    ]);
    const updateLabRequestIsMutating = useIsMutating([
        "update-draft-lab-request",
    ]);
    const signLabRequestIsMutating = useIsMutating(["sign-draft-lab-request"]);
    const isSubmitting =
        createLabRequestIsMutating > 0 ||
        updateLabRequestIsMutating > 0 ||
        signLabRequestIsMutating > 0;
    const isLoadingTests =
        labCategoriesResponse.isLoading || labTestsResponse.isLoading;
    const testsError = labCategoriesResponse.error ?? labTestsResponse.error;

    return (
        <>
            {showSignDialog ? (
                <Dialog open>
                    <DialogContent
                        title="Sign Lab Request"
                        variant="center"
                        saveText="Sign"
                        showFooter
                        submitBtnFormValue={SIGN_LAB_REQUEST_FORM_ID}
                        isSubmitBtnDisabled={isSubmitting}
                        isCancelBtnDisabled={isSubmitting}
                        handleCloseDialog={() => {
                            setShowSignDialog(false);
                            if (!labRequest && draftLabRequestId.current) {
                                navigate(
                                    `/lab-requests/${draftLabRequestId.current}/edit`,
                                    { replace: true }
                                );
                            }
                        }}
                    >
                        <SignLabRequestForm onSubmit={signSavedLabRequest} />
                    </DialogContent>
                </Dialog>
            ) : null}
            <form
                onSubmit={handleSubmit(saveDraftLabRequest, showFormErrorToast)}
            >
                {clientId ? (
                    <div className="mb-4">
                        <h2 className="text-title font-medium">
                            Lab request for
                            <Link
                                className="text-primary underline inline-block ml-1 underline-offset-2"
                                to={`/clients/${clientId}`}
                            >
                                {clientName}
                            </Link>
                        </h2>
                    </div>
                ) : null}
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            {!clientId ? (
                                <Popover
                                    open={showClientPopover}
                                    onOpenChange={(open) => {
                                        setShowClientPopover(open);
                                        setClientSearchString("");
                                    }}
                                >
                                    <PopoverTrigger
                                        asChild
                                        onClick={() =>
                                            setShowClientPopover(true)
                                        }
                                    >
                                        <FilterButton
                                            type="button"
                                            className={
                                                selectedClient
                                                    ? "capitalize"
                                                    : "normal-case"
                                            }
                                        >
                                            {selectedClient
                                                ? displayNameInRightFormat({
                                                      firstName:
                                                          selectedClient.first_name,
                                                      lastName:
                                                          selectedClient.last_name,
                                                  })
                                                : "Select Client"}
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <SearchInput
                                            placeholder="Search by name"
                                            onChange={(e) =>
                                                setClientSearchString(
                                                    e.target.value
                                                )
                                            }
                                        />

                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            {clients.isLoading ? (
                                                <Loader wrapperClassName="h-[150px]" />
                                            ) : null}
                                            {clients.error && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        clients.error?.response
                                                            ?.data?.message ||
                                                        "Error loading clients"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}
                                            {clients?.data?.data &&
                                                clients.isSuccess && (
                                                    <>
                                                        <Button
                                                            type="button"
                                                            size="auto"
                                                            variant="can-hover"
                                                            className="font-medium capitalize w-full justify-start shrink-0 mt-2"
                                                            onClick={() => {
                                                                setSelectedClient(
                                                                    null
                                                                );
                                                                setValue(
                                                                    "client_id",
                                                                    "",
                                                                    {
                                                                        shouldDirty:
                                                                            true,
                                                                        shouldValidate:
                                                                            true,
                                                                    }
                                                                );
                                                                setShowClientPopover(
                                                                    false
                                                                );
                                                                setClientSearchString(
                                                                    ""
                                                                );
                                                            }}
                                                        >
                                                            Select Client
                                                        </Button>
                                                        {clients?.data?.data.map(
                                                            (client) => (
                                                                <Button
                                                                    type="button"
                                                                    key={
                                                                        client.client_id
                                                                    }
                                                                    size="auto"
                                                                    variant="can-hover"
                                                                    className="font-medium capitalize w-full justify-start shrink-0"
                                                                    onClick={() => {
                                                                        setSelectedClient(
                                                                            {
                                                                                client_id:
                                                                                    client.client_id,
                                                                                first_name:
                                                                                    client.first_name,
                                                                                last_name:
                                                                                    client.last_name,
                                                                            }
                                                                        );
                                                                        setValue(
                                                                            "client_id",
                                                                            client.client_id,
                                                                            {
                                                                                shouldDirty:
                                                                                    true,
                                                                                shouldValidate:
                                                                                    true,
                                                                            }
                                                                        );
                                                                        setShowClientPopover(
                                                                            false
                                                                        );
                                                                        setClientSearchString(
                                                                            ""
                                                                        );
                                                                    }}
                                                                >
                                                                    {displayNameInRightFormat(
                                                                        {
                                                                            firstName:
                                                                                client.first_name,
                                                                            lastName:
                                                                                client.last_name,
                                                                        }
                                                                    )}
                                                                    {selectedClient?.client_id ===
                                                                    client.client_id ? (
                                                                        <CheckIcon className="size-4" />
                                                                    ) : null}
                                                                </Button>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            ) : null}
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        type="button"
                                        disabled={labCategories.length === 0}
                                    >
                                        {selectedLabCategory?.name ??
                                            "Lab Category"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="250px">
                                    <DropdownItem
                                        onClick={() =>
                                            handleFilterChange(
                                                "lab_category",
                                                "All"
                                            )
                                        }
                                        isSelected={!labCategoryFilter}
                                    >
                                        All
                                    </DropdownItem>
                                    {labCategories.map((category) => (
                                        <DropdownItem
                                            key={category.lab_category_id}
                                            onClick={() =>
                                                handleFilterChange(
                                                    "lab_category",
                                                    category.lab_category_id
                                                )
                                            }
                                            isSelected={
                                                category.lab_category_id ===
                                                labCategoryFilter
                                            }
                                        >
                                            {category.name}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <div className="flex items-center gap-x-[--spacing-items]">
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={isSubmitting}
                            >
                                Save Draft
                            </Button>
                            <Button
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleSubmit(
                                    saveDraftBeforeSigning,
                                    showFormErrorToast
                                )}
                            >
                                Save and Sign
                            </Button>
                        </div>
                    </div>
                </Card>
                <div>
                    {isLoadingTests ? <Loader /> : null}
                    {testsError ? (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page to solve this issue"
                            errorMsg={
                                testsError.response?.data.message ??
                                `Cannot display tests at this time`
                            }
                        />
                    ) : null}
                    {!isLoadingTests && !testsError ? (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-[--spacing-items]">
                            {availableTestsByCategory.map(
                                ({ category, tests }) => {
                                    const categoryLabTestIds = tests.map(
                                        (test) => test.lab_test_id
                                    );
                                    const areAllCategoryTestsSelected =
                                        categoryLabTestIds.every((labTestId) =>
                                            selectedTests.includes(labTestId)
                                        );

                                    return (
                                        <Card key={category.lab_category_id}>
                                            <h3 className="text-base font-medium  mb-[--spacing-items] text-textSec">
                                                {category.name}
                                            </h3>
                                            <div className="flex flex-col gap-y-2">
                                                <FormCheckbox
                                                    label="Select All"
                                                    checked={
                                                        areAllCategoryTestsSelected
                                                    }
                                                    onChange={(event) =>
                                                        handleCategoryTestsChange(
                                                            categoryLabTestIds,
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                                {tests.map((test) => (
                                                    <FormCheckbox
                                                        key={test.lab_test_id}
                                                        label={
                                                            test.display_name
                                                        }
                                                        checked={selectedTests.includes(
                                                            test.lab_test_id
                                                        )}
                                                        onChange={(event) =>
                                                            handleTestChange(
                                                                test.lab_test_id,
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Card>
                                    );
                                }
                            )}
                        </div>
                    ) : null}
                </div>
            </form>
        </>
    );
}
